import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react';
import {
  I18N_STORAGE_KEY,
  LANGUAGES,
  translations,
  type Language
} from './translations';
import { LanguageContext } from './LanguageContext';
import { getInitialLanguage } from './languageUtils';

type LanguageProviderProps = {
  children: ReactNode;
};

const LANGUAGE_SLIDE_DURATION_MS = 900;
const LANGUAGE_CHANGE_DELAY_MS = 380;
const LANGUAGE_OVERLAY_EXIT_DELAY_MS = 80;
const languageSlideTransition = {
  duration: LANGUAGE_SLIDE_DURATION_MS / 1000,
  ease: [0.76, 0, 0.24, 1]
} as const;

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);
  const [transitionKey, setTransitionKey] = useState<number | null>(null);
  const prefersReducedMotion = useReducedMotion();
  const languageRef = useRef(language);
  const pendingLanguageRef = useRef<Language | null>(null);
  const changeLanguageTimeoutRef = useRef<number | null>(null);
  const clearOverlayTimeoutRef = useRef<number | null>(null);

  const clearTransitionTimers = useCallback(() => {
    if (changeLanguageTimeoutRef.current !== null) {
      window.clearTimeout(changeLanguageTimeoutRef.current);
      changeLanguageTimeoutRef.current = null;
    }

    if (clearOverlayTimeoutRef.current !== null) {
      window.clearTimeout(clearOverlayTimeoutRef.current);
      clearOverlayTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    languageRef.current = language;
    document.documentElement.lang = language;
    document.documentElement.dir = 'ltr';
    window.localStorage.setItem(I18N_STORAGE_KEY, language);
  }, [language]);

  useEffect(() => {
    return () => clearTransitionTimers();
  }, [clearTransitionTimers]);

  const setLanguage = useCallback(
    (nextLanguage: Language) => {
      if (
        languageRef.current === nextLanguage ||
        pendingLanguageRef.current === nextLanguage
      ) {
        return;
      }

      clearTransitionTimers();
      pendingLanguageRef.current = nextLanguage;

      if (prefersReducedMotion) {
        languageRef.current = nextLanguage;
        pendingLanguageRef.current = null;
        setTransitionKey(null);
        setLanguageState(nextLanguage);
        return;
      }

      setTransitionKey(previousKey => (previousKey ?? 0) + 1);

      changeLanguageTimeoutRef.current = window.setTimeout(() => {
        const pendingLanguage = pendingLanguageRef.current;

        if (!pendingLanguage) {
          return;
        }

        languageRef.current = pendingLanguage;
        pendingLanguageRef.current = null;
        setLanguageState(pendingLanguage);
      }, LANGUAGE_CHANGE_DELAY_MS);

      clearOverlayTimeoutRef.current = window.setTimeout(() => {
        setTransitionKey(null);
        clearOverlayTimeoutRef.current = null;
      }, LANGUAGE_SLIDE_DURATION_MS + LANGUAGE_OVERLAY_EXIT_DELAY_MS);
    },
    [clearTransitionTimers, prefersReducedMotion]
  );

  const value = useMemo(
    () => ({
      language,
      languages: LANGUAGES,
      setLanguage,
      t: translations[language]
    }),
    [language, setLanguage]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
      <AnimatePresence>
        {transitionKey !== null ? (
          <motion.div
            key={transitionKey}
            aria-hidden="true"
            className="pointer-events-auto fixed inset-0 z-[9999] overflow-hidden"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
          >
            <motion.div
              className="absolute inset-y-0 left-0 w-[115vw] bg-[#007d9b] shadow-[36px_0_90px_rgba(0,187,195,0.28)] dark:bg-[#00bbc3]"
              initial={{ x: '-110%', skewX: -5 }}
              animate={{ x: '110%', skewX: -5 }}
              transition={languageSlideTransition}
            >
              <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(255,255,255,0.24),rgba(255,255,255,0.06)_42%,rgba(255,255,255,0.16))]" />
              <div className="absolute inset-y-0 right-0 w-24 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.36))]" />
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </LanguageContext.Provider>
  );
};
